<!--
  Forbrukerrådet - Kommunetesten
  @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
  @author Anders Evenrud <anders.evenrud@copyleft.no>
-->
<template>
  <div :class="{ 'search': true, 'search--focused': focused }">
    <input
      type="text"
      :placeholder="placeholder"
      :value="query"
      :aria-label="placeholder"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      @keydown.up="onUp"
      @keydown.down="onDown"
      @keydown.escape="onEscape"
      @keydown.enter.prevent="onEnter" />

    <font-awesome-icon icon="search" />

    <div class="search__items" :style="dropdownStyles" v-if="query.length > 1">
      <div v-if="!hasResults" class="search__items__empty">
        Ingen resultater for <strong>&quot;{{ query }}&quot;</strong>
      </div>

      <ul>
        <li v-for="(item, index) in searchResultsGrouped" :key="item.kid ? item.kid : item.fid" :class="itemClass(item, index)" @click="onClick">
          <span>
            <template v-if="typeof item === 'string'">
              {{ item }}
            </template>

            <template v-else>
              <div class="search__item" @click="onItemClick($event, item)">
                <Shield :county="item.fid" :municipality="item.kid" :title="item.name" />

                <span class="search__item__name" v-if="item.name">{{ item.name }} ({{ item.countyName }})</span>
                <span class="search__item__name" v-else>{{ item.countyName }}</span>
                <span class="search__item__score" v-if="item.points">Poeng: {{ decimalLabel(item.points) }}</span>
              </div>
            </template>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/variables";
@import "../assets/mixins";

.search {
  @include input($search-border);
  background: $search-background;
  display: flex;
  align-items: center;
  font-size: 120%;

  & > input {
    display: block;
    flex: 1 1;
    width: 100%;
    border: 0 none;
    margin: 0;
    padding: 0;
    background: transparent;
    box-sizing: border-box;
    outline: 0 none;
    font-size: $font-size;
    font-family: inherit;
    color: inherit;
    min-height: 1.25em;
    line-height: normal;

    &::placeholder {
      color: inherit;
    }
  }

  &--focused {
    border: 1px solid #04a4cc;
  }

  &__items {
    position: absolute;
    top: 100%;
    width: 100%;
    left: 0;
    z-index: 9000;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);
    max-height: 80vh;
    overflow: auto;
    background: $search-fill;

    &__empty {
      padding: 1em;
      margin: 0;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style-type: none;
      background-color: $list-primary-background;

      li {
        margin: 0;

        &:not(:last-child) {
          border-bottom: $list-border-color;
        }
      }
    }

    &__header {
      padding: 0.5em;
      font-size: 80%;
      color: $link-text-color;
      background-color: $list-header-background;
    }

    &__row {
      padding: 0;

      &--selected {
        background-color: $list-active-background !important;
      }

      &:nth-child(2n+2) {
        background-color: $list-secondary-background;
      }

      &:hover {
        background-color: $list-active-background;
      }
    }
  }
}

.search__item {
  padding: 0.6em;
  cursor: pointer;
  display: flex;
  align-items: center;

  &__name {
    flex: 1 1;
    margin-left: 0.5em;
  }
}
</style>

<script>
import Shield from './Shield.vue'
import { EventBus } from '../bus.js'
import { debounce } from 'lodash-es'

export default {
  components: {
    Shield
  },

  props: {
    year: Number,
    placeholder: String
  },

  computed: {
    dropdownStyles () {
      return {
        display: this.active ? 'block' : 'none'
      }
    },

    searchResults () {
      return this.$store.getters.searchData(this.query, this.year)
    },

    searchResultsGrouped () {
      const results = this.searchResults
      let grouped = []

      if (results.municipalities.length > 0) {
        grouped = [
          'Kommuner',
          ...results.municipalities
        ]
      }

      if (results.counties.length > 0) {
        grouped = grouped.concat([
          'fylker',
          ...results.counties
        ])
      }

      return grouped
    },

    hasResults () {
      const results = this.searchResults

      return results.counties.length > 0 ||
        results.municipalities.length > 0
    }
  },

  methods: {
    clear () {
      this.active = false
      this.selected = -1
      this.query = ''
    },

    onDown () {
      let selected = this.selected + 1

      if (typeof this.searchResultsGrouped[selected] === 'string') {
        selected++
      }

      this.selected = selected > this.searchResultsGrouped.length - 1
        ? -1
        : selected
    },

    onUp () {
      let selected = this.selected - 1

      if (typeof this.searchResultsGrouped[selected] === 'string') {
        selected--
      }

      this.selected = selected < -1
        ? this.searchResultsGrouped.length - 1
        : selected
    },

    onClick () {
      this.clear()
    },

    onInput: debounce(function (ev) {
      this.onInputDebounce(ev)
    }, 100),

    onInputDebounce (ev) {
      this.selected = -1
      this.query = ev.target.value.trim()
      this.active = this.query.length > 0

      this.$emit('oninput', ev, this.query)
    },

    onEscape () {
      this.clear()
    },

    onBlur (ev) {
      this.focused = false
    },

    onFocus (ev) {
      if (this.query && !this.active) {
        this.onInput(ev)
      }

      this.focused = true
    },

    onEnter (ev) {
      if (this.selected !== -1) {
        const item = this.searchResultsGrouped[this.selected]

        this.onItemClick(ev, item)
      }

      this.clear()
    },

    onItemClick (ev, item) {
      this.$emit('select', item)
    },

    itemClass (item, index) {
      if (typeof item === 'string') {
        return 'search__items__header'
      }

      const name = 'search__items__row'
      const append = this.selected === index
        ? `${name}--selected`
        : ''

      return [name, append]
    }
  },

  created () {
    EventBus.$on('body-clicked', ev => {
      if (this.active) {
        const hit = ev.target === this.$el ||
          this.$el.contains(ev.target)

        if (!hit) {
          this.active = false

          this.$el.querySelector('input[type=text]').blur()
        }
      }
    })
  },

  data () {
    return {
      query: '',
      active: false,
      focused: false,
      selected: -1
    }
  }
}
</script>
